import * as THREE from "three";
import { groundAt } from "./terrain";
import { Enemy, LevelSim, SimEvent, SimEventKind, isBeam, isMortar } from "@highmarch/domain";
import { ELEMENT } from "../../../../art/vfx.js";
import { PooledVfx } from "./vfxPool";
import { hqTowerName, lib, muzzleFallback } from "./views";
import { ProjectileViews, RELEASE_FPS, ShotHandle, TOWER_SHOT, UNIT_SHOT, releaseSeconds } from "./views/projectiles";
import { towerFireRate } from "./views/towers";
import { IMPACT, LIGHTNING_RELEASE_FRAME, PET_ROUND, STATUS_OF_TOWER, StatusBit, impactGate, shotKey, statusGate, tierScale } from "./statusFx";
import { reducedVfx } from "../ui/settings";
import { WaveFx } from "./waveFx";
import { BeaconAuras } from "./beaconAuras";
import { AirAim, DEATH_LIFT, DrawnFeet, ROUND_LIFT, aimAt, STRIKE_LANDS_WITHIN, WASTED_BOMB, byPet, byRound, chainTargets, enemyThisTick, feetOf, follow, pierced, roundFlight, wastedBombLanding } from "./vfxRouting";
import { VfxLights } from "./vfxLights";
import { StrikeLight } from "./strikeLight";
import { FxHooks, StrikeBeats } from "./fxHooks";
import { HitStop } from "../level/hitStop";
import { loadVfxAtlas } from "./vfxAtlas";

export { DEATH_LIFT, WASTED_BOMB_HOP, enemyThisTick, follow, roundFlight, wastedBombLanding } from "./vfxRouting";

/**
 * Routes sim events to the art session's sprite VFX (art/vfx.js, art/README.md §5): real rounds for the shooters
 * (views/projectiles.ts), tesla arcs for lightning, and for the element towers (vfx.js ELEMENT) a frost crystal that
 * charges before its shard leaves, rounds and impacts sized by tier, and the beacon's standing aura (it never fires:
 * its rune circle and pulse are drawn every frame in `update`); kills, placements and upgrades. Pure presentation;
 * the legacy particle system in effects.ts stays as the fallback.
 *
 * It also decides the weight of a beat (art_review/vfx.md F1, F8): the VFX module queues camera trauma, which
 * `update` hands to `addTrauma` (the level's IsoCamera); and this class asks the level's `hitStop` for the pauses —
 * the fortress strike 70 ms, a boss killed 250 ms plus a slow-motion ramp, a cannon round that kills two or more
 * 40 ms when it lands.
 *
 * And it decides when a shot's damage shows (statusFx.ts ImpactGate): the enemy view and the sound routing queue a
 * shot's hits and kills on the gate, and this class lands them as the round does — a projectile's impact, each victim
 * of a ballista bolt as it passes, each chain hop as its arc reaches it, the fortress strike's bolt — and lands a
 * hitscan shot at the end of the tick.
 */
export class VfxRouter {
  /** Real projectile models (v6); the level adds `projectiles.group` to the scene and calls `update`. */
  readonly projectiles: ProjectileViews;
  /** Set by the level: world position of a named node on a tower's / enemy's model, after the rig update. */
  towerNode: ((towerId: number, suffix: string) => THREE.Vector3 | null) | null = null;
  enemyNode: ((enemyId: number, suffix: string) => THREE.Vector3 | null) | null = null;
  /**
   * Set by the level: seconds a tower's shot waits for its weapon to come round (views/towerAim.ts: the tower view
   * delays the `fire` clip by as much), and which way its weapon points at the release (the round leaves along it).
   */
  towerAimDelay: ((towerId: number) => number) | null = null;
  towerAimDir: ((towerId: number) => THREE.Vector3 | null) | null = null;
  /**
   * Set by the level: where the enemy views draw an enemy (EnemyViews.feet). A body killed by a round in flight walks
   * on until the round lands; the round flies at it and its death bursts where it is then, not where the sim killed it.
   */
  enemyFeet: DrawnFeet | null = null;
  enemyAir: AirAim | null = null;
  /**
   * Set by the level: whether this tick's shot by the fortress `towerId` is its strike (TowerViews.heroShot) or a zap
   * between strikes (views/towers.ts FortressRhythm). Unset, every fortress shot is the strike.
   */
  baseShotIsHero: ((towerId: number) => boolean) | null = null;
  /** Set by the level: a camera kick (seconds, strength) for the wave's big moments. */
  onShake: ((seconds: number, strength: number) => void) | null = null;
  /** Wave start, emergence, per-type deaths and fortress hits. */
  private readonly wave: WaveFx;
  /** The beacons' standing auras (beaconAuras.ts). */
  private readonly beacons: BeaconAuras;
  /** Set by the level: camera trauma sink (IsoCamera.addTrauma, skipped under reduced motion). */
  addTrauma: ((amount: number) => void) | null = null;
  /** Set by the level: its hit-stop clock. */
  hitStop: HitStop | null = null;
  /** Set by the level: the current quality tier index (0 ultra … 4 lowest), for the particle budget. */
  qualityTier: (() => number) | null = null;
  /** Kills per tower in the tick being routed, and each tower's last round in flight (for the splash hit-stop). */
  private readonly killsThisTick = new Map<number, number>();
  private readonly lastShot = new Map<number, ShotHandle>();
  /** Each tower's latest round's key: a burn tick's kill waits on it (as views/enemies.ts EnemyViews.lastRound). */
  private readonly lastRound = new Map<number, number>();
  /**
   * The sprite atlas (2048² RGBA with mips, ~21 MiB of GPU memory), loaded per level; the router owns it and frees it
   * in `dispose` — `VFX.dispose` frees its meshes and materials but not the texture it was handed.
   */
  private readonly atlas: THREE.Texture | null;
  private constructor(readonly vfx: PooledVfx, private readonly sim: LevelSim, atlas: THREE.Texture | null = null) {
    this.atlas = atlas;
    this.projectiles = new ProjectileViews(() => this.vfx, reducedVfx);
    this.wave = new WaveFx(vfx, sim, (seconds, strength) => this.onShake?.(seconds, strength));
    this.beacons = new BeaconAuras(vfx, sim);
  }

  /** Loads the atlas (through the inlined store for single-file builds); null when it is missing. */
  static async create(scene: THREE.Scene, camera: THREE.Camera, sim: LevelSim): Promise<VfxRouter | null> {
    try {
      const { meta, texture: tex } = await loadVfxAtlas();
      return new VfxRouter(new PooledVfx(scene, camera, tex, meta), sim, tex);
    } catch (err) {
      console.warn("vfx atlas failed to load; using the built-in particles", err);
      return null;
    }
  }

  onEvent(ev: SimEvent): boolean {
    const sim = this.sim;
    switch (ev.kind) {
      case SimEventKind.TowerFired: {
        // TOWERS_V2: the Mortar's shell flies on the sim's clock from ShellLaunched and hits on ShellLanded, and an
        // Arcane Spire's TowerFired is a pulse of its held beam: both are drawn by render/towersV2Fx.ts, and their
        // hits show on the tick the sim deals them (nothing waits on a round here)
        const fired = sim.findTower(ev.a), fa = fired ? sim.balance.towers[fired.archetype] : null;
        if (fa && (isMortar(fa) || isBeam(fa))) return true;
        this.lastRound.set(ev.a, shotKey(sim.tick, ev.a));
        const t = sim.findTower(ev.a); const e = enemyThisTick(sim, ev.b);       // a killing shot's target is already dead
        if (!t || !e) return true;
        const arch = sim.balance.towers[t.archetype];
        const id = arch.id;
        const to = aimAt(e, ROUND_LIFT, this.enemyAir);              // a flyer's body, else just over the ground
        // the stale table's muzzle, only for a tower whose model has no measured one: made on first ask, not every shot
        let fallbackAt: THREE.Vector3 | null = null;
        const fallback = (): THREE.Vector3 => (fallbackAt ??= muzzleFallback(id, t.tier, t.cell, Math.atan2(to.x - t.cell.x, to.z - t.cell.y)));
        const targetOf = (enemyId: number) => follow(sim, enemyId, ROUND_LIFT, to, this.enemyFeet, this.enemyAir);
        if (arch.isBase) {                                                  // the fortress strike (DEV_HANDOFF §3)
          const tip = this.towerNode?.(t.id, "condgem0") ?? fallback();
          // between strikes: one bolt from the conductor, no beats (the sim fires the fortress 2-5 times a second)
          if (!(this.baseShotIsHero?.(t.id) ?? true)) { this.vfx.baseZap(tip, to, { scale: tierScale(t.tier) }); return true; }
          const stop = e.isBoss ? Math.max(HitStop.Ms.baseStrike, HitStop.Ms.bossHeavyHit) : HitStop.Ms.baseStrike;
          // leader 100 ms, then on the frame the bolt lands: hit-stop, the strike light, exposure punch, screen flash
          const key = shotKey(sim.tick, t.id);
          impactGate.expect(key, STRIKE_LANDS_WITHIN);
          this.vfx.baseStrike(tip, to, { onStrike: (at) => { this.strikeBeats(at, stop); impactGate.land(key); } });
          return true;
        }
        const shot = TOWER_SHOT[id];
        if (shot) {                                                         // a real round, on the clip's release frame
          // the clip plays faster when the tower fires faster than it lasts (towerFireRate): the release comes sooner
          const rate = towerFireRate(sim, t);
          const pierce = arch.pierceCount > 0 ? pierced(sim, t.id, this.enemyAir) : null;
          const delay = this.towerAimDelay?.(t.id) ?? 0;
          const opts = { rate, through: pierce?.at, delay, barrel: () => this.towerAimDir?.(t.id) ?? null };
          // the element towers' rounds grow with the tier; the frost crystal visibly gathers cold until the release
          const bit = STATUS_OF_TOWER[id], k = bit ? tierScale(t.tier) : 1;
          if (id === "frost") this.vfx.frostCharge(this.towerNode?.(t.id, shot.muzzle ?? "") ?? fallback(), releaseSeconds(shot, rate) + delay, k);
          const handle = this.projectiles.fire(shot, () => this.towerNode?.(t.id, shot.muzzle ?? "") ?? null, targetOf(e.id), fallback(), k, opts);
          // the hits and kills show when the round lands (each pierced victim as the bolt passes it), and the slow /
          // burn / shred too, not the tick the sim applied them; if the round never flies, by when it would have landed
          const key = shotKey(sim.tick, t.id), enemyId = e.id;
          impactGate.expect(key, delay + releaseSeconds(shot, rate) + roundFlight(fallback(), pierce?.at.length ? pierce.at : [to], shot.speed, !!pierce?.at.length) + IMPACT.graceSeconds);
          // Frost's burst chills everything near its target (combat.ts splash): those slows land with the shard too,
          // and a frost ring the size of the burst shows the area on the ground
          const chilled = id === "frost" && arch.splashRadius.isPositive() ? chilledBy(sim, t.id, enemyId) : [];
          const chillSize = arch.splashRadius.toNumber() * 2.2;
          handle.onImpact = () => {
            if (bit && this.sim.findEnemy(enemyId)) statusGate.land(enemyId, bit);
            for (const other of chilled) if (this.sim.findEnemy(other)) statusGate.land(other, StatusBit.Frost);
            if (chillSize > 0) {
              const at = this.sim.findEnemy(enemyId);
              const p = at ? new THREE.Vector3(at.x.toNumber(), groundAt(at.x.toNumber(), at.y.toNumber()) + 0.05, at.y.toNumber()) : to;
              this.vfx.ring(p, { size: chillSize * k, life: 0.45, color: [0.55, 0.85, 1.0], bright: 1.8 });
            }
            impactGate.land(key);
          };
          if (pierce) handle.onPass = (i) => impactGate.land(key, pierce.ids[i]);
          this.lastShot.set(t.id, handle);
          return true;
        }
        // the arcs leave the crystal on the clip's release frame and chain on, hop by hop
        if (id === "lightning") {
          const delay = LIGHTNING_RELEASE_FRAME / RELEASE_FPS / Math.max(1, towerFireRate(sim, t));
          const chain = chainTargets(sim, ev.a, ev.b, to, this.enemyAir);
          this.vfx.teslaFire(this.towerNode?.(t.id, "crystal") ?? fallback(), chain.at, { delay, scale: tierScale(t.tier) });
          // each victim shows its hit as its arc reaches it (vfx.js teslaFire: hop i lands i · hopDelay after the release)
          const key = shotKey(sim.tick, t.id), hop = ELEMENT.lightning.hopDelay;
          chain.ids.forEach((enemyId, i) => impactGate.landIn(key, enemyId, delay + i * hop));
          impactGate.expect(key, delay + chain.ids.length * hop + IMPACT.graceSeconds);   // hops past the drawn ones
          return true;
        }
        this.vfx.towerFire(id, this.towerNode?.(t.id, "orb") ?? fallback(), to);   // beacon and anything hitscan
        return true;
      }
      case SimEventKind.BombDropped: {                                       // bomber lobs at a tower
        const b = enemyThisTick(sim, ev.a);                                 // the bomb falls as the bomber dies or leaks
        if (!b) return true;
        const bx = b.x.toNumber(), bz = b.y.toNumber();
        const from = new THREE.Vector3(bx, groundAt(bx, bz) + 0.14, bz);
        const t = ev.b > 0 ? sim.findTower(ev.b) : null;
        if (t) {
          const target = new THREE.Vector3(t.cell.x, groundAt(t.cell.x, t.cell.y) + 0.6, t.cell.y);
          this.projectiles.fire(UNIT_SHOT.bomber, () => this.enemyNode?.(b.id, "bomb") ?? null, () => target, from);
          return true;
        }
        // Wasted (tower 0: nothing in reach): the bomb is still thrown and bursts on the ground a short hop from where
        // the bomber fell, toward the nearest tower when there is one, so the player sees it fizzle.
        const land = wastedBombLanding(bx, bz, sim.towerSlots.filter((x) => x.alive).map((x) => x.cell));
        const target = new THREE.Vector3(land.x, groundAt(land.x, land.z), land.z);
        this.projectiles.fire(WASTED_BOMB, () => this.enemyNode?.(b.id, "bomb") ?? null, () => target, from);
        return true;
      }
      case SimEventKind.EnemyHealed: {                                       // healer's charm flies to the healed unit
        if (ev.b <= 0) return true;
        const h = sim.findEnemy(ev.b);
        if (!h) return true;
        const from = new THREE.Vector3(h.x.toNumber(), groundAt(h.x.toNumber(), h.y.toNumber()) + 0.2, h.y.toNumber());
        const targetOf = () => { const x = sim.findEnemy(ev.a); return x ? new THREE.Vector3(x.x.toNumber(), groundAt(x.x.toNumber(), x.y.toNumber()) + 0.15, x.y.toNumber()) : null; };
        this.projectiles.fire(UNIT_SHOT.healer, () => this.enemyNode?.(h.id, "weapon") ?? null, targetOf, from);
        return true;
      }
      case SimEventKind.EnemyKilled: {
        const e = sim.enemySlots.find((x) => x.id === ev.a);
        if (ev.b > 0) this.killsThisTick.set(ev.b, (this.killsThisTick.get(ev.b) ?? 0) + 1);
        if (!e) return true;
        // the death's beats play when the killing round lands (the enemy view holds the body there until then); the
        // body's slot may be reused by a new spawn by then, so what they need is read now
        const body = { archetype: e.archetype, isBoss: e.isBoss, x: e.x, y: e.y };
        const at = new THREE.Vector3(e.x.toNumber(), groundAt(e.x.toNumber(), e.y.toNumber()) + DEATH_LIFT, e.y.toNumber());
        const id = e.id;
        // where the body is when the round lands: it walked on since the sim killed it
        const show = () => { const d = this.enemyFeet?.(id); if (d) at.set(d.x, groundAt(d.x, d.z) + DEATH_LIFT, d.z); this.died(body, at); };
        if (byRound(sim, ev) || byPet(sim, ev)) impactGate.wait(shotKey(sim.tick, ev.b > 0 ? ev.b : PET_ROUND), e.id, show);   // a round, the pet's blow
        else if (ev.b > 0 && this.lastRound.has(ev.b)) impactGate.wait(this.lastRound.get(ev.b)!, e.id, show);   // a burn tick's kill
        else show();
        return true;
      }
      case SimEventKind.EnemySpawned: this.wave.spawned(ev.b, ev.c === 1); return true;
      case SimEventKind.EnemyLeaked: this.wave.leaked(); return true;
      case SimEventKind.TroopAttacked: {
        const e = enemyThisTick(sim, ev.b);
        if (e) this.vfx.meleeHit(new THREE.Vector3(e.x.toNumber(), groundAt(e.x.toNumber(), e.y.toNumber()) + 0.05, e.y.toNumber()));
        return true;
      }
      case SimEventKind.TowerPlaced: this.vfx.towerPlace(new THREE.Vector3(ev.b, groundAt(ev.b, ev.c), ev.c)); return true;
      case SimEventKind.TowerUpgraded: {
        const t = sim.findTower(ev.a);
        if (!t) return true;
        this.vfx.towerUpgrade(new THREE.Vector3(t.cell.x, groundAt(t.cell.x, t.cell.y), t.cell.y));
        // Tier-up burst at ~55 % of the model's height (art/interface/README.md drop 2 §4).
        const a = sim.balance.towers[t.archetype];
        const h = lib()?.height(hqTowerName(a.id, a.isBase, t.tier)) ?? 1.2;
        this.vfx.tierUp(new THREE.Vector3(t.cell.x, groundAt(t.cell.x, t.cell.y) + h * 0.55, t.cell.y), 1.6);
        return true;
      }
      case SimEventKind.BossSpawned: {
        if (this.bossViaView) return true;                                  // the units view's onBossSpawn drives it at the boss
        const p = sim.road.portal; this.vfx.bossArrival(new THREE.Vector3(p.x, groundAt(p.x, p.y) + 0.05, p.y)); return true;
      }
      case SimEventKind.WavesStarted: return true;                          // always followed by StageStarted in the same tick
      case SimEventKind.StageStarted: {
        // one beat: our portal flare (light + glow) and WaveFx's surge rings, sparkles and its single camera kick
        const p = sim.road.portal;
        this.vfx.waveStart(new THREE.Vector3(p.x, groundAt(p.x, p.y), p.y));
        this.wave.stageStarted();
        return true;
      }
      default: return false;
    }
  }

  /** A kill's beats, at the body's last spot: its per-type ring and sparks, the puff, and a boss's fall and hit-stop. */
  private died(body: Pick<Enemy, "archetype" | "isBoss" | "x" | "y">, at: THREE.Vector3): void {
    this.wave.killed(body, at);                                          // per-type ring and sparks (waveFx.ts)
    if (body.isBoss) {                                                   // the boss falls: 0.7 trauma, 250 ms + slow-mo
      if (!this.bossViaView) this.vfx.bossFall(at);                      // else the units view's onBossDeath drives it
      this.hitStop?.request(HitStop.Ms.bossDeath, { ramp: true, force: true });
      return;
    }
    // The puff and sparkle of the death, and no coin: the reward is the level header's to show (the points pill
    // counts up and its coin glints, ui/hud.ts), as the owner asked (2026-09-24). The kill's "+N" rises from the
    // enemy marks layer (EnemyViews.floatAt), not the atlas digits (vfx.md F9), so it is drawn once.
    this.vfx.unitDeath(at, { coins: 0 });
  }

  /** A new surge within the wave: the portal pulses. */
  surge(): void { this.wave.surge(); }

  /** True once the level routes boss arrival/fall from the units view's boss hooks (at the boss, not the portal). */
  bossViaView = false;

  /** Boss views from the units view (`onBossSpawn` / `onBossDeath`): the arrival burst and the fall. */
  bossArrived(root: THREE.Object3D): void { this.vfx.bossArrival(feetOf(root), { size: Math.max(1, root.scale.x) }); }
  bossFell(root: THREE.Object3D, at?: THREE.Vector3 | null): void { this.vfx.bossFall(at ? at.clone() : feetOf(root), { size: Math.max(1, root.scale.x) }); }
  /** The fortress strike's beats outside the sprite module, on the frame the main bolt lands. */
  private strikeBeats(at: THREE.Vector3, stopMs: number): void {
    this.hitStop?.request(stopMs);
    const L = StrikeBeats.light;
    StrikeLight.fire(at.clone().setY(at.y + L.lift), L.colour, L.intensity, L.range, L.seconds);
    FxHooks.exposurePunch?.(StrikeBeats.exposure.gain, StrikeBeats.exposure.ms);
    FxHooks.screenFlash?.(StrikeBeats.flash.color, StrikeBeats.flash.peak, StrikeBeats.flash.ms);
  }

  /** Called before a tick's events are routed. */
  beginTick(): void { this.killsThisTick.clear(); }

  /** Called after: a cannon round that killed two or more gets a 40 ms hit-stop when it lands. */
  endTick(): void {
    for (const [towerId, kills] of this.killsThisTick) {
      if (kills < 2) continue;
      const t = this.sim.findTower(towerId);
      if (!t || this.sim.balance.towers[t.archetype].id !== "cannon") continue;
      const shot = this.lastShot.get(towerId);
      if (shot) { const landed = shot.onImpact; shot.onImpact = () => { landed?.(); this.hitStop?.request(HitStop.Ms.splashMultiKill); }; }
    }
    this.killsThisTick.clear();
    impactGate.settle();                                                 // hitscan shots: their hits show now
  }

  update(dt: number): void {
    if (this.qualityTier) this.vfx.setQuality(this.qualityTier());
    this.beacons.update(dt);
    this.vfx.update(dt);
    this.projectiles.update(dt);
    impactGate.update(dt);                                               // after the rounds: a landing beats its deadline
    this.wave.update(dt);
    const trauma = this.vfx.takeTrauma();
    if (trauma > 0) this.addTrauma?.(trauma);
  }

  /** Samples the level's height field for the ground decals: the grid plus a two-cell apron, four samples a cell. */
  setGround(width: number, height: number): void { this.vfx.setGround(groundAt, -2, -2, width + 1, height + 1, 4); }

  /** Pushes the live VFX light slots to the shared shader uniforms; call after the camera's final move of the frame. */
  syncLights(camera: THREE.Camera): void { VfxLights.sync(this.vfx.lights, camera); }

  /** Removes the sprite layers, decals and any live bolts/beams from the scene, and turns the lights off. */
  /** Frees the atlas's GPU copy (it keeps its image, so a later draw uploads it again): the rig is resting (M33). */
  releaseGpu(): void { this.atlas?.dispose(); }

  dispose(scene: THREE.Scene): void {
    this.projectiles.dispose();
    scene.remove(this.projectiles.group);
    this.vfx.dispose();
    this.atlas?.dispose();
    VfxLights.clear();
    this.lastShot.clear();
    this.lastRound.clear();
    this.beacons.clear();
    statusGate.reset();
    impactGate.reset();
  }
}

/** The enemies a frost shot of this tick chilled besides its target (the splash's EnemySlowed events, combat.ts). */
function chilledBy(sim: LevelSim, towerId: number, targetId: number): number[] {
  const out: number[] = [];
  for (const ev of sim.events.all()) if (ev.kind === SimEventKind.EnemySlowed && ev.b === towerId && ev.a !== targetId && !out.includes(ev.a)) out.push(ev.a);
  return out;
}
